import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Settings, ShoppingCart, Package, List, Menu, X, CreditCard, Home } from 'lucide-react';
import { NeedHelpLink } from '@/components/ui/NeedHelpLink';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils'; // Optional utility to merge classNames

interface DashboardHeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ activeTab, setActiveTab }) => {
  const { theme, setTheme } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const mobileSidebarRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleNav = (tab: string) => {
    setActiveTab(tab);
    setIsMobileOpen(false); // auto-close sidebar on small devices after selection
  };

  const NavItem = ({
    icon: Icon,
    label,
    tab,
  }: {
    icon: any;
    label: string;
    tab: string;
  }) => (
    <div
      onClick={() => handleNav(tab)}
      className={cn(
        'flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all',
        activeTab === tab
          ? 'bg-primary/10 text-primary'
          : 'hover:text-primary hover:bg-accent'
      )}
    >
      <Icon className="h-4 w-4" />
      {!isCollapsed && <span>{label}</span>}
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileSidebarRef.current && !mobileSidebarRef.current.contains(event.target as Node)) {
        setIsMobileOpen(false);
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOpen]);

  return (
    <>
      {/* Toggle Button for Mobile */}
      <button
        className="md:hidden fixed top-[50px] left-4 p-2 transition-all z-40"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-screen bg-sidebar border-r border-sidebar-border p-4 z-40 transition-all duration-300',
          isCollapsed ? 'w-20' : 'w-56',
          'hidden md:block', // hidden on mobile
        )}
      >
        <div className="flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center space-x-2 mb-6">
              {!isCollapsed && (
                <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                  KeepMore
                </span>
              )}
            </div>
            <nav className="flex flex-col gap-2 font-medium text-muted-foreground">
              <NavItem icon={Home} label="Savings Calculator" tab="savings_calculator" />
              <NavItem icon={ShoppingCart} label="Checkout Pages" tab="bundles" />
              <NavItem icon={Package} label="Products" tab="products" />
              <NavItem icon={CreditCard} label="Subscriptions" tab="subscriptions" />
              <NavItem icon={List} label="Transactions" tab="transactions" />
            </nav>
          </div>
          <div className="flex flex-col font-medium gap-2">
            <NavItem icon={Settings} label="Settings" tab="settings" />
            <hr className="border-border" />
            <span
              onClick={handleLogout}
              className="flex items-center gap-1 text-muted-foreground hover:text-destructive hover:bg-accent p-2 rounded-md cursor-pointer transition-all"
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span>Log Out</span>}
            </span>
            <NeedHelpLink showText={!isCollapsed} />
          </div>
        </div>
      </aside>

      {isMobileOpen && (
        <div
          ref={mobileSidebarRef}
          className="fixed top-0 left-0 h-screen w-64 bg-background p-4 z-50 shadow-apple-lg md:hidden transition-all duration-300"
        >
          <div className="flex flex-col h-full justify-between">
            <div>
              <div className="flex justify-between items-center mb-6">
                <div className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">KeepMore</div>
                <button
                  className="p-2 transition-all"
                  onClick={() => setIsMobileOpen(false)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-2 text-muted-foreground">
                <NavItem icon={Home} label="Savings Calculator" tab="savings_calculator" />
                <NavItem icon={ShoppingCart} label="Checkout Pages" tab="bundles" />
                <NavItem icon={Package} label="Products" tab="products" />
                <NavItem icon={CreditCard} label="Subscriptions" tab="subscriptions" />
                <NavItem icon={List} label="Transactions" tab="transactions" />
              </nav>
            </div>
            <div className="flex flex-col gap-2 text-muted-foreground">
              <NavItem icon={Settings} label="Settings" tab="settings" />
              <hr className="border-border" />
              <span
                onClick={handleLogout}
                className="flex items-center gap-1 text-muted-foreground hover:text-destructive hover:bg-accent p-2 rounded-md cursor-pointer transition-all"
              >
                <LogOut className="h-4 w-4" />
                <span>Log Out</span>
              </span>
              <NeedHelpLink />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardHeader;